import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppError } from "@/jenesys-types/errors";

// State stores a mapping from transactionId to URL for caching
interface DocumentPreviewState {
  isLoading: boolean;
  error?: AppError;
  documentImageURLs: Record<string, string>; // transactionId -> url
}

const initialState: DocumentPreviewState = {
  isLoading: false,
  error: undefined,
  documentImageURLs: {}
};

const name = "features/transactions/documentPreview";

export interface LoadDocumentImageURLPayload {
  companyId: string;
  documentId: string;
}

const documentPreviewSlice = createSlice({
  name,
  initialState,
  reducers: {
    // Called when a document preview is requested
    loadDocumentImageURL: (
      state,
      _action: PayloadAction<LoadDocumentImageURLPayload>
    ) => {
      // Only set loading if not cached; component logic will handle which id is being viewed
      state.isLoading = true;
      state.error = undefined;
    },
    // Called when the URL is successfully fetched
    loadDocumentImageURLSuccess: (
      state,
      action: PayloadAction<{ id: string; url: string }>
    ) => {
      state.isLoading = false;
      state.documentImageURLs[action.payload.id] = action.payload.url;
    },
    // Called when there is an error fetching the URL
    loadDocumentImageURLFailure: (state, action: PayloadAction<AppError>) => {
      state.isLoading = false;
      state.error = action.payload;
    }
  }
});

export const {
  loadDocumentImageURL,
  loadDocumentImageURLSuccess,
  loadDocumentImageURLFailure
} = documentPreviewSlice.actions;

// Selector to get the whole document preview state
export const selectDocumentPreviewState = (state: any) =>
  state.features.transactions.documentPreview;
// Selector to get the cached URL for a given transactionId
export const selectDocumentImageURLById = (id: string) => (state: any) =>
  selectDocumentPreviewState(state).documentImageURLs[id];
// Selector to get loading state
export const selectDocumentImageIsLoading = (state: any) =>
  selectDocumentPreviewState(state).isLoading;
// Selector to get error state
export const selectDocumentImageError = (state: any) =>
  selectDocumentPreviewState(state).error;

export default documentPreviewSlice.reducer;
